import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useDvojbojRanking } from '../../api/hooks/useDvojbojRanking';
import {
  AGE_SUBCATEGORY_LABELS,
  AGE_SUBCATEGORY_ORDER,
} from '../../types/individualEntry';
import type { DvojbojRanking } from '../../types/dvojboj';

interface Props {
  categoryId: number;
  disciplineLabels?: string[];
}

const MEDAL_COLORS = {
  1: { bg: 'rgba(255, 215, 0, 0.20)', text: '#7a5c00', label: 'Zlato' },
  2: { bg: 'rgba(192, 192, 192, 0.28)', text: '#525252', label: 'Stříbro' },
  3: { bg: 'rgba(205, 127, 50, 0.22)', text: '#6b3a0c', label: 'Bronz' },
} as const;

/**
 * 6 mini-tabulek pořadí dvojboje per `age_subcategory`.
 * Sloupce: Pořadí | Jméno | SDH | Disc.1 P | Disc.2 P | ... | Součet bodů.
 *
 * Highlight medailových pozic 1.–3.
 *
 * @param categoryId ID kategorie
 * @param disciplineLabels labely disciplín dvojboje (např. ['100m překážek', '100m PHP']);
 *                         pokud chybí, použijí se generická 'P1', 'P2' atd.
 */
export function DvojbojRankingTable({ categoryId, disciplineLabels }: Props) {
  const { data, isLoading } = useDvojbojRanking(categoryId);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  const rankings = data ?? [];
  if (rankings.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Pořadí dvojboje není k dispozici. Závodníci musí absolvovat všechny disciplíny dvojboje
          (alespoň 2 disciplíny s příznakem dvojboje).
        </Typography>
      </Paper>
    );
  }

  // Seřaď mini-rankings dle preferovaného pořadí podkategorií
  const sorted = [...rankings].sort(
    (a, b) =>
      AGE_SUBCATEGORY_ORDER.indexOf(a.ageSubcategory) -
      AGE_SUBCATEGORY_ORDER.indexOf(b.ageSubcategory),
  );

  return (
    <Stack spacing={3}>
      {sorted.map((ranking) => (
        <DvojbojMiniTable
          key={ranking.ageSubcategory}
          ranking={ranking}
          disciplineLabels={disciplineLabels}
        />
      ))}
    </Stack>
  );
}

function DvojbojMiniTable({
  ranking,
  disciplineLabels,
}: {
  ranking: DvojbojRanking;
  disciplineLabels?: string[];
}) {
  const discCount = ranking.items[0]?.ranksInDiscs.length ?? 0;
  const labels =
    disciplineLabels && disciplineLabels.length === discCount
      ? disciplineLabels
      : Array.from({ length: discCount }, (_, i) => `Disc. ${i + 1} P`);

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmojiEventsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            {AGE_SUBCATEGORY_LABELS[ranking.ageSubcategory]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({ranking.items.length}{' '}
            {ranking.items.length === 1
              ? 'závodník'
              : ranking.items.length < 5
                ? 'závodníci'
                : 'závodníků'}
            )
          </Typography>
        </Stack>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: 80 }}>Pořadí</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Jméno</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>SDH</TableCell>
            {labels.map((label, idx) => (
              <TableCell key={idx} align="right" sx={{ fontWeight: 700 }}>
                {label}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Součet bodů
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ranking.items.map((item) => {
            const medal = MEDAL_COLORS[item.overallRank as 1 | 2 | 3];
            return (
              <TableRow
                key={item.individualEntryId}
                sx={
                  medal
                    ? {
                        bgcolor: medal.bg,
                        '& td': { fontWeight: 600 },
                      }
                    : undefined
                }
              >
                <TableCell sx={{ fontWeight: 700, color: medal?.text }}>
                  {item.overallRank}.
                </TableCell>
                <TableCell>
                  {item.lastName} {item.firstName}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{item.sdhUnitName}</TableCell>
                {item.ranksInDiscs.map((rank, idx) => (
                  <TableCell key={idx} align="right" sx={{ fontFamily: 'monospace' }}>
                    {rank}.
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {item.sumPoints}
                </TableCell>
              </TableRow>
            );
          })}
          {ranking.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={4 + labels.length} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">Žádní hodnocení závodníci</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
